import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import _, { map, omit } from 'lodash';
import {
  type TReservationSetting,
  reservationMappingSelector,
  reservationSettingsSelector,
  typeIdentifiedSelector,
} from 'slices/reservationSettings.slice';
import {
  Col,
  Form,
  Row,
  Select,
  Typography,
} from '@timeedit/ui-components/antd';

const { Title, Text } = Typography;
type Object = { [key: string]: any };
type ModeField = {
  optionsSource: string;
  required: boolean;
  label: string;
};

type Props = {
  modeFieldId: string;
  fields: Object;
  types: Object;
  label: string;
  onChange: (data: TReservationSetting) => void;
};

const SelectModeTemplate = ({
  types,
  fields,
  modeFieldId,
  label,
  onChange,
}: Props) => {
  const reservationMapping = useSelector(reservationMappingSelector);
  const reservationSettings = useSelector(reservationSettingsSelector);
  const typeIdentified = useSelector(typeIdentifiedSelector);

  const { modeField, propertyFields, modeFieldValue } = useMemo(
    () => ({
      modeField: fields[modeFieldId],
      propertyFields: omit(fields, [modeFieldId]),
      modeFieldValue: (
        reservationSettings.find(
          ({ appFieldId }) => appFieldId === modeFieldId
        ) || {}
      ).fieldExt,
    }),
    [fields, modeFieldId, reservationSettings]
  );

  const renderItem = useCallback(
    ({ optionsSource, required, label }: ModeField, appFieldId: string) => {
      const options =
        optionsSource === 'situationFields'
          ? _.get(reservationMapping, [optionsSource, modeFieldValue!], [])
          : _.get(reservationMapping, [optionsSource], []);

      return (
        <Form.Item
          label={label}
          key={appFieldId}
          rules={[
            {
              required:
                appFieldId === modeFieldId ||
                modeField.required ||
                modeFieldValue
                  ? required
                  : false,
              message: '',
            },
          ]}
          name={appFieldId}
        >
          <Select
            onChange={(value) => {
              onChange({ appFieldId, fieldExt: value });
            }}
            allowClear
            showSearch
            disabled={!modeFieldValue && appFieldId !== modeFieldId}
          >
            {(options || []).map(
              (
                { label, value }: { label: string; value: string },
                index: number
              ) => (
                <Select.Option key={index} value={value}>
                  {label}
                </Select.Option>
              )
            )}
          </Select>
        </Form.Item>
      );
    },
    [modeFieldId, modeFieldValue, reservationMapping]
  );

  return (
    <div>
      <div>
        {label && <Title level={3}>{label}</Title>}
        {renderItem(modeField, modeFieldId)}
      </div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4}>Types</Title>
        {map(types, (type, typeKey) => (
          <Row key={typeKey}>
            <Col
              sm={24}
              md={12}
              lg={8}
              className={type.required && 'suffix-required'}
            >
              {type.label}
            </Col>
            <Col sm={24} md={12} lg={16}>
              {_.get(typeIdentified, [modeFieldId, typeKey]) ? (
                <Text style={{ color: 'green' }}>
                  Identified on reservation mode
                </Text>
              ) : (
                <Text type="danger">Not identified on reservation mode</Text>
              )}
            </Col>
          </Row>
        ))}
      </div>
      <div>
        <Title level={4}>Fields</Title>
        {map(propertyFields, (field, appFieldId) =>
          renderItem(field, appFieldId)
        )}
      </div>
    </div>
  );
};

export default SelectModeTemplate;
